import React from 'react'
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableContainer,
  TableRow,
  TableCell,
  Checkbox,
  Typography,
  TablePagination
} from '@mui/material'

const UI = {
  COLORS: { headerBg: '#f7f7f9', rowHover: 'rgba(0,89,246,0.03)', border: '#e7edf6' },
  WIDTHS: {
    checkbox: 48,
    fecha: 100,
    nombre: 150,
    numero: 120,
    consulta: 150,
    mail: 180,
    origen: 150,
  },
}

const cellBase = {
  fontSize: '12px',
  borderColor: UI.COLORS.border,
  py: 1.25,
  px: 1,
  color: '#06185f',
}

const headerCellBase = {
  fontSize: '12px',
  borderColor: UI.COLORS.border,
  py: 1.35,
  px: 1,
  fontWeight: 900,
  color: '#06185f',
}

function formatFecha(fechaStr) {
  if (!fechaStr) return ''
  const d = new Date(fechaStr)
  if (isNaN(d)) return ''
  const pad = (n) => String(n).padStart(2, '0')
  const dd = pad(d.getDate())
  const mm = pad(d.getMonth() + 1)
  const aa = String(d.getFullYear()).slice(-2)
  const hh = pad(d.getHours())
  const min = pad(d.getMinutes())
  return `${dd}/${mm}/${aa} ${hh}:${min}`
}

function formatPhone(raw) {
  const d = String(raw || '').replace(/\D/g, '')
  const a = d.slice(0, 3)
  const b = d.slice(3, 6)
  const c = d.slice(6, 10)
  return b ? `${a} ${b}${c ? '' + c : ''}` : a
}

export default function ConsultasNoAsignTable({
  clientes = [],
  selectedIds = [],
  highlightConsultaId = null,
  onToggleAll,
  onToggleRow,
  count = 0,
  page = 0,
  rowsPerPage = 20,
  onPageChange,
  customHeight,
}) {
  const allChecked = clientes.length > 0 && selectedIds.length === clientes.length
  const indeterminate = selectedIds.length > 0 && selectedIds.length < clientes.length
  const isFlexMode = customHeight === "flex";
  const rowRefs = React.useRef({});

  React.useEffect(() => {
    if (!highlightConsultaId) return;
    const node = rowRefs.current[String(highlightConsultaId)];
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightConsultaId, clientes]);

  return (
    <Box sx={{
      overflow: 'hidden',
      display: isFlexMode ? 'flex' : 'block',
      flexDirection: 'column',
      flex: isFlexMode ? 1 : 'none',
      minHeight: 0,
      backgroundColor: 'rgba(255,255,255,0.96)',
      border: '1px solid #dce5f2',
      borderRadius: '16px',
      boxShadow: '0 20px 44px rgba(19, 39, 84, 0.11)',
      boxSizing: 'border-box',
    }}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: '16px 16px 8px 8px',
          border: `1px solid ${UI.COLORS.border}`,
          flex: isFlexMode ? 1 : 'none',
          minHeight: 0,
          overflowY: 'auto', 
          maxHeight: isFlexMode ? 'none' : "56vh",
          backgroundColor: '#fff',
          transition: "flex 0.3s ease"
        }}
      >
        <Table size="small" aria-label="consultas no asignadas" sx={{ tableLayout: 'fixed' }} stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { borderBottom: `1px solid ${UI.COLORS.border}` } }}>
              <TableCell
                padding="checkbox"
                sx={{
                  ...headerCellBase,
                  width: UI.WIDTHS.checkbox,
                  backgroundColor: UI.COLORS.headerBg,
                  borderRight: `1px solid ${UI.COLORS.border}`,
                }}
              >
                <Checkbox
                  size="small"
                  checked={allChecked}
                  indeterminate={indeterminate}
                  onChange={(e) => onToggleAll?.(e.target.checked)}
                />
              </TableCell>

              <TableCell
                sx={{
                  ...headerCellBase,
                  width: UI.WIDTHS.fecha,
                  backgroundColor: UI.COLORS.headerBg,
                  borderRight: `1px solid ${UI.COLORS.border}`,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Fecha carga
              </TableCell>

              <TableCell
                sx={{
                  ...headerCellBase,
                  width: UI.WIDTHS.nombre,
                  backgroundColor: UI.COLORS.headerBg,
                  borderRight: `1px solid ${UI.COLORS.border}`,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Nombre
              </TableCell>

              <TableCell
                sx={{
                  ...headerCellBase,
                  width: UI.WIDTHS.numero,
                  backgroundColor: UI.COLORS.headerBg,
                  borderRight: `1px solid ${UI.COLORS.border}`,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Número
              </TableCell>

              <TableCell
                sx={{
                  ...headerCellBase,
                  width: UI.WIDTHS.consulta,
                  backgroundColor: UI.COLORS.headerBg,
                  borderRight: `1px solid ${UI.COLORS.border}`,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Consulta
              </TableCell>

              <TableCell
                sx={{
                  ...headerCellBase,
                  width: UI.WIDTHS.mail,
                  backgroundColor: UI.COLORS.headerBg,
                  borderRight: `1px solid ${UI.COLORS.border}`,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Mail
              </TableCell>

              <TableCell
                sx={{
                  ...headerCellBase,
                  width: UI.WIDTHS.origen,
                  backgroundColor: UI.COLORS.headerBg,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Obtenido de
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {/* Estado vacío */}
            {clientes.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 6, borderBottom: `1px solid ${UI.COLORS.border}` }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography variant="h6" sx={{ opacity: 0.8 }}>
                      No hay consultas sin asignar
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.6 }}>
                      Cuando se carguen consultas nuevas aparecerán aquí.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {clientes.map((c) => {
              const checked = selectedIds.includes(c.id)
              const consultaId = c.id_ven_cli ?? c.consulta_id ?? c.id
              const highlighted = highlightConsultaId != null && String(consultaId) === String(highlightConsultaId)
              return (
                <TableRow
                  key={c.id}
                  ref={(node) => {
                    if (node) rowRefs.current[String(consultaId)] = node;
                    else delete rowRefs.current[String(consultaId)];
                  }}
                  hover
                  sx={{
                    transition: 'background-color 180ms ease, box-shadow 180ms ease, opacity 120ms ease',
                    '&:hover': { backgroundColor: UI.COLORS.rowHover },
                    backgroundColor: highlighted ? '#fff7d6' : '#fff',
                    boxShadow: highlighted ? 'inset 4px 0 0 #f59f00' : 'none',
                    '& td': { borderBottom: `1px solid ${UI.COLORS.border}` },
                  }}
                >
                  <TableCell padding="checkbox" sx={{ ...cellBase }}>
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={(e) => onToggleRow?.(c.id, e.target.checked)}
                    />
                  </TableCell>

                  <TableCell sx={{ ...cellBase }}>
                    {formatFecha(c.fecha_carga)}
                  </TableCell>

                  <TableCell
                    sx={{
                      ...cellBase,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={c.nombre ?? ''}
                  >
                    {c.nombre ?? ''}
                  </TableCell>

                  <TableCell
                    sx={{
                      ...cellBase,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={c.telefono ? formatPhone(c.telefono) : ''}
                  >
                    {c.telefono ? formatPhone(c.telefono) : ''}
                  </TableCell>

                  <TableCell
                    sx={{
                      ...cellBase,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={c.consulta ?? ''}
                  >
                    {c.consulta ?? ''}
                  </TableCell>

                  <TableCell
                    sx={{
                      ...cellBase,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={c.email ?? ''}
                  >
                    {c.email ?? ''}
                  </TableCell>

                  <TableCell
                    sx={{
                      ...cellBase,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={c.obtenido_de ?? ''}
                  >
                    {c.obtenido_de ?? ''}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: 'background.paper',
          border: `1px solid ${UI.COLORS.border}`,
          borderTop: 0,
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          
        }}
      >
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
        />
      </Box>
    </Box>
  )
}
